import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Box, FormControl, IconButton, MenuItem, Select, Stack, Switch, TextField } from '@mui/material';

import {
  type PackageInclusionRecord,
  getInclusionCategory,
  getInclusionName,
  isInclusionHidden,
  updateInclusionCategory,
  updateInclusionName,
} from '../../../utils';

import { PACKAGE_INCLUSIONS_GRID_COLUMNS } from './utils';

interface Props {
  categoryOptions: Array<string>;
  id: string;
  inclusion: PackageInclusionRecord;
  onChange: (nextInclusion: PackageInclusionRecord) => void;
  onDelete: () => void;
}

export default function PackageInclusionsTabRow({ categoryOptions, id, inclusion, onChange, onDelete }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const isHidden = isInclusionHidden(inclusion);
  const category = getInclusionCategory(inclusion);
  const resolvedCategoryOptions = categoryOptions.includes(category) ? categoryOptions : [category, ...categoryOptions];

  return (
    <Box
      ref={setNodeRef}
      sx={{
        alignItems: 'center',
        borderTop: 1,
        borderColor: 'divider',
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', lg: PACKAGE_INCLUSIONS_GRID_COLUMNS },
        opacity: isDragging ? 0.72 : isHidden ? 0.58 : 1,
        px: 2,
        py: 2,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <IconButton
        size="small"
        aria-label={`Reorder ${getInclusionName(inclusion) || 'inclusion'}`}
        sx={{ color: 'text.disabled', cursor: isDragging ? 'grabbing' : 'grab' }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>

      <FormControl size="small" fullWidth disabled={isHidden}>
        <Select value={category} onChange={(event) => onChange(updateInclusionCategory(inclusion, event.target.value))}>
          {resolvedCategoryOptions.map((category) => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        value={getInclusionName(inclusion)}
        onChange={(event) => onChange(updateInclusionName(inclusion, event.target.value))}
        size="small"
        fullWidth
        disabled={isHidden}
      />

      <Stack direction="row" justifyContent={{ xs: 'flex-start', lg: 'center' }}>
        <Switch
          checked={inclusion.style === 'highlighted'}
          onChange={(event) => onChange({ ...inclusion, style: event.target.checked ? 'highlighted' : null })}
          disabled={isHidden || !!inclusion.lux_plus_tier}
        />
      </Stack>

      <Stack direction="row" justifyContent={{ xs: 'flex-start', lg: 'center' }}>
        <Switch
          checked={inclusion.lux_plus_tier === 'base'}
          onChange={(event) => onChange({ ...inclusion, lux_plus_tier: event.target.checked ? 'base' : null })}
          disabled={isHidden || inclusion.style === 'highlighted' || inclusion.style === 'bonus'}
        />
      </Stack>

      <IconButton color="error" aria-label="Delete inclusion" onClick={onDelete} disabled={isHidden}>
        <DeleteIcon />
      </IconButton>
    </Box>
  );
}
